<script setup lang="ts">
import { modelIconForRow } from "@/composables/modelIcons";
import EditableSearchTableHeader from '@/components/DataTable/EditableSearchTableHeader.vue';
import SearchMapView from '@/components/SearchMapView.vue';
import { buildSearchFilterPayload, useSearchApi } from '@/composables/api/SearchApi';
import TrackListExportControls from '@/components/TrackListExportControls.vue';
import SyncStatusButton from '@/components/Sync/SyncStatusButton.vue';
import { showsSyncPill } from '@/utils/syncStatus';
import type { TrackListExportSource } from '@/types/trackListExport';
import { storeToRefs } from 'pinia';
import { computed, nextTick, provide, reactive, ref, toRaw, watch } from 'vue';
import { dropPlaylistContainerRequest, requestPlaylistContainerSection } from '@/composables/playlistContainerSectionRequest';
import { useEntityStore } from '@/stores/entityStore';
import { useRoutedSavedView } from '@/composables/useRoutedSavedView';
import { useRoute as useListRoute, useRouter as useListRouter } from 'vue-router';
import type { RowGesture } from '@/components/DataTable/rowClick';
import { resolveRowGesture } from '@/composables/rowGestureOnScreen';
import { useLongPressDelegate } from '@/composables/useLongPressDelegate';
import { listSearchRule, listSearchText, withListSearch } from '@/utils/listSearch';
import { hiddenMatch, provideQuickSearchMatches } from '@/composables/quickSearchMatches';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import { LIST_HIGHLIGHT } from '@/utils/highlightMatch';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import { recordListState } from '@/composables/useRecordListContext';
import { areaEntity, recordPathFor } from '@/navigation/areas';
import DataTable from '@/components/DataTable/DataTable.vue';
import SearchCardsSplitView from '@/components/DataTable/SearchCardsSplitView.vue';
import RecordPane from '@/components/record/RecordPane.vue';
import CombinedRecordPane from '@/components/record/CombinedRecordPane.vue';
import ListRowPaneScope from '@/components/DataTable/ListRowPaneScope.vue';
import { createPaneTrail, LIST_PANE_HOST } from '@/composables/recordPaneTrail';
import { entityDisplayNameFor } from '@/composables/entityColumns';
import TableRecordSplitView from '@/components/DataTable/TableRecordSplitView.vue';
import { listSelectionKeyAction } from '@/components/DataTable/tableSelectionKeys';
import SearchResultListRow from '@/components/DataTable/SearchResultListRow.vue';
import ListGroupHeader from '@/components/DataTable/ListGroupHeader.vue';
import { groupHeadersFor } from '@/components/DataTable/groupBoundaries';
import {
  cardFixedColumnIdsFor,
  fixedCardHeaderComponentFor,
  cardShownMatchFields,
  isPlaylistContainerEntity,
  inspectorExcludeFieldsFor,
} from '@/composables/searchInspectorRegistry';
import LocationZonePlayerCardHeader from '@/components/DataTable/LocationZonePlayerCardHeader.vue';
import PlaylistContainerCardHeader from '@/components/DataTable/PlaylistContainerCardHeader.vue';
import CardTags from '@/components/CardTags.vue';
import LzpBulkIssueBar from '@/components/DataTable/LzpBulkIssueBar.vue';
import SearchLivePollingIndicator from '@/components/DataTable/SearchLivePollingIndicator.vue';
import { useSearchResultsLivePolling } from '@/composables/api/useSearchResultsLivePolling';
import ColumnMapping from '@/components/ColumnMapping.vue';
import PlayTrackButton from '@/components/PlayTrackButton.vue';
import CoverArtTile from '@/components/CoverArtTile.vue';
import LzpCurrentlyPlayingCard from '@/components/DataTable/LzpCurrentlyPlayingCard.vue';
import { useCurrentTrackSummaries } from '@/composables/api/useCurrentTrackSummaries';
import { useInspectorStore } from '@/stores/inspectorStore';
import { useInspectorTopOffset } from '@/composables/useInspectorTopOffset';
import { useEventListener, useMediaQuery } from '@vueuse/core';
import { usePersistentTable } from '@/composables/usePersistentTable';
import { useCursorPager } from '@/composables/useCursorPager';
import { toBulkSelectionId, useBulkSelection } from '@/composables/useBulkSelection';
import BulkActionBar from '@/components/BulkActions/BulkActionBar.vue';
import type { BulkBarScheduleSelection } from '@/components/BulkActions/bulkSelectionText';
import type { MixedRunKind } from '@/composables/useMixedBulkRun';
import { useMatchingScheduleCount } from '@/composables/useMatchingScheduleCount';
import { cardSchedulesFor, cardSchedulesKeyOf } from '@/components/schedules/useCardSchedules';
import type { ScheduleEntry } from '@/components/schedules/scheduleGroups';
import { provideCardSchedules } from '@/components/schedules/cardSchedulesContext';
import TableScheduleSortScope from '@/components/schedules/TableScheduleSortScope.vue';
import { usePermissions } from '@/composables/usePermissions';
import { getSearchTableCustomColumns } from '@/composables/searchTableCustomColumns';
import { exportColumnOptionsFor, isExportedByDefault } from '@/composables/exportColumns';
import { readNestedId } from '@/utils/nestedRelation';
import { provideTrackQueueContext } from '@/composables/trackQueueContext';
import type { TableColumn } from '@/types/tableTypes';

const entityStore = useEntityStore();
const { entitySlug, selectedEntity, selectedEntityId, entities } = storeToRefs(entityStore);

/**
 * Zero permitted models (SAU-287): the permission filter left nothing to list.
 * Rendered as its own panel instead of a dead table.
 */
const hasNoPermittedEntities = computed(() => entities.value.length === 0);

/** Per-entity non-model columns (currently just Play for LiveTrack / RawDdexTrack). */
const searchCustomColumns = computed(() => getSearchTableCustomColumns(selectedEntityId.value));

/** <PlayTrackButton> props for a search row, by the selected entity. */
function playButtonPropsForRow(row: Record<string, unknown>) {
  const title = (row.name as string) ?? (row.title as string) ?? null;
  const coverUrl =
    (row.cover_art_signed_url as string) ?? (row.cover_art_url as string) ?? null;
  // LiveTrack search docs nest artist/album as {id, display_string};
  // RawDdexTrack stores `album_name` (string) and `artists` (array). Try the
  // nested shape first and fall back to the raw columns so the player gets
  // the right strings regardless of which entity drives the search.
  const artist = readNestedDisplay(row.artist) ?? readArtistsArray(row.artists);
  const album =
    readNestedDisplay(row.album) ?? ((row.album_name as string | null | undefined) ?? null);

  if (selectedEntityId.value === 'RawDdexTrack') {
    return {
      kind: 'raw_ddex' as const,
      rawDdexTrackId: (row.id as number | string) ?? null,
      hasAudio: Boolean(row.audio_file_s3_path),
      title,
      artist,
      album,
      coverUrl,
    };
  }
  // LiveTrack
  return {
    kind: 'live_track' as const,
    liveTrackCode: (row.code as string) ?? null,
    title,
    artist,
    album,
    coverUrl,
    // Records for the player bar's identity lines. Only the nested
    // {id, display_string} shape carries ids; a row that fell back to a plain
    // `album_name` string has none, and that line stays plain text.
    liveTrackId: (row.id as number | string) ?? null,
    artistId: readNestedId(row.artist),
    albumId: readNestedId(row.album),
  };
}

function readNestedDisplay(value: unknown): string | null {
  if (value && typeof value === 'object' && 'display_string' in value) {
    const ds = (value as { display_string?: unknown }).display_string;
    return typeof ds === 'string' && ds.trim() !== '' ? ds : null;
  }
  return null;
}

function readArtistsArray(value: unknown): string | null {
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (first && typeof first === 'object' && 'name' in first) {
      const name = (first as { name?: unknown }).name;
      return typeof name === 'string' && name.trim() !== '' ? name : null;
    }
  }
  return null;
}

const MAP_ENTITY_IDS = ['Account', 'Location', 'LocationZone'] as const;
const canUseMapView = computed(() =>
  MAP_ENTITY_IDS.includes(selectedEntityId.value as (typeof MAP_ENTITY_IDS)[number]),
);

// `/players?view=12` opens view 12, and picking a view updates the address.
// Only a list route names its entity; tests that mount the page bare do not.
const listRoute = useListRoute();
const onEntityListRoute = typeof listRoute?.meta?.entityId === 'string';
const routedView = onEntityListRoute ? useRoutedSavedView() : null;
const listRouter = onEntityListRoute ? useListRouter() : null;

/**
 * Open a row on its own page. The rows on screen go with it in the history
 * state, so the record page can show "1 of 20" and step through them, and its
 * breadcrumb names this list: the saved view if one is open, else the entity.
 */
function openRecordPage(row: Record<string, unknown>) {
  const recordId = Number(row.id);
  if (!listRouter || !Number.isInteger(recordId)) return;
  const ids = displayRows.value.map((candidate: Record<string, unknown>) => Number(candidate.id)).filter(Number.isInteger);
  const label = routedView?.activeView.value?.name ?? areaEntity(selectedEntityId.value)?.label ?? selectedEntityId.value;
  void listRouter.push({
    path: recordPathFor(selectedEntityId.value, recordId),
    state: recordListState({ ids, label, path: listRoute.fullPath }),
  });
}

const { openRecord } = useOpenRecordInInspector();


const {
  columns,
  pinnedColumns,
  unpinnedColumns,
  visiblePinnedColumns,
  visibleNonPinnedColumns,
  cardVisibleColumns,
  // The sort the server gets: the user's sort, with the Group by column first.
  effectiveSort: columnSort,
  groupBy,
  compositeFilter,
  pageIndex,
  pageSize,
  toggleSort,
  setColumnSize,
  updateColumnState,
  searchViewMode,
  tableLayout,
  filter: tableFilter,
  setFilter: setTableFilter,
} = usePersistentTable({ additionalColumns: searchCustomColumns });

// `/players?q=rau` arrives from the search page's "Open in list". The text
// becomes the list's search, exactly as if typed into its search box, and
// leaves the address, since the filter now holds it.
if (onEntityListRoute && typeof listRoute.query.q === 'string' && listRoute.query.q.trim() !== '') {
  const searchRule = listSearchRule(listRoute.query.q.trim());
  setTableFilter(withListSearch(tableFilter.value, searchRule));
  const restOfQuery = { ...listRoute.query };
  delete restOfQuery.q;
  void listRouter?.replace({ query: restOfQuery });
}

const effectiveViewMode = computed(() => {
  if (canUseMapView.value && searchViewMode.value === 'map') {
    return 'map';
  }
  return tableLayout.value === 'cards' || tableLayout.value === 'fixed-cards'
    ? 'cards'
    : 'list';
});

/**
 * The result set on screen, as the `source` node the export and play-queue
 * endpoints share: which entity, the current filter, the current sort.
 *
 * Every searchable entity, not just Live Tracks. The filter goes through the
 * same cleaning the search request gets, so a half-built rule the results
 * ignore is ignored here too — sending the raw builder state would name a
 * different list from the one on screen.
 *
 * Null only while no list entity has resolved, which is the
 * zero-grant case the page already renders its own panel for.
 */
const searchSource = computed<TrackListExportSource | null>(() =>
  entitySlug.value
    ? {
        type: 'search',
        entity: entitySlug.value,
        filter: buildSearchFilterPayload(compositeFilter.value),
        sort: columnSort.value,
      }
    : null,
);

/** "Live Track search", "Account search" — matching what the backend names it. */
const searchLabel = computed(
  () => `${selectedEntity.value?.entityDisplayName ?? 'Search'} search`,
);

/**
 * Play-in-context for search results: clicking Play on a row queues the whole
 * result set behind the current filter and sort. Read as a getter at click
 * time so a filter change between mount and click queues the list the user is
 * actually looking at.
 *
 * Live Tracks ONLY, unlike the export above. A queue entry is an audio file
 * plus the fields a track card renders, so there is nothing to play in an
 * Account search — the endpoint refuses one, and a RawDdexTrack row falls back
 * to playing exactly one track.
 */
provideTrackQueueContext(() =>
  selectedEntityId.value === 'LiveTrack' && searchSource.value
    ? {
        source: searchSource.value,
        label: searchLabel.value,
        // The pager's last known total. It is 0 until a response carries one and
        // is reset to 0 on any filter/sort change, so it can only ever say "1"
        // about the result set on screen — and a result set of one has nothing
        // behind the clicked row to fetch.
        knownTotal: cachedTotal.value,
      }
    : null,
);

/**
 * The columns on screen, in their order, on the layout the user is looking at.
 * The export column list starts with these ticked until the user has exported
 * this record type once. After that it starts from their last export choice.
 * The table reads its own Column Settings
 * (pinned first, as rendered). The card layouts read the card set — the
 * always-on header fields first, then the opt-in summary columns — which is
 * the order Column Settings lists them in for cards.
 *
 * Custom display columns (Play) are left out: they are buttons, not data.
 * A login password starts unticked, so the workbook carries it only when the
 * person ticks it. Nothing else is filtered here. Which columns a spreadsheet can carry is the
 * exporter's rule, and this page does not keep a copy of it — it sends what
 * the user is looking at and the endpoint says what it took and what it left.
 */
const exportColumnIds = computed<string[]>(() => {
  const dataColumnIds = (exportColumns: TableColumn[]): string[] =>
    exportColumns.filter(isExportedByDefault).map((column) => column.id);

  // The plain Cards layout is the ONLY one whose card column picker is the
  // live control, so it is the only one whose card set describes what the user
  // chose. Map view and the Fixed Card layout also render this toolbar, and
  // both used to fall in here: Accounts on the map exported `id` and `name`
  // while every pin card on screen showed the address, and the Fixed Card
  // exported two columns for a card showing thirteen — with Column Settings
  // hidden in that layout, so there was nothing the user could do about it.
  //
  // Everywhere else the table's Column Settings is the last column choice the
  // user actually made, so that is what the workbook carries.
  if (effectiveViewMode.value === 'cards' && tableLayout.value === 'cards') {
    return [...cardFixedColumnIdsFor(selectedEntityId.value), ...dataColumnIds(cardColumns.value)];
  }

  return dataColumnIds([...visiblePinnedColumns.value, ...visibleNonPinnedColumns.value]);
});

/**
 * Every column the export column list offers on this page, with its name.
 * The columns above only decide which ones start ticked.
 */
const exportColumnOptions = computed(() => exportColumnOptionsFor(columns.value));

/**
 * Compact cover thumb for the REGULAR cards view (the fixed card brings its
 * own full-size cover): entities whose rows carry playable cover art get a
 * small leading tile — the same play-overlay + expand-to-zoom cover chain,
 * sized to the header row plus one summary line.
 */
const showCoverThumb = computed(
  () => selectedEntityId.value === 'LiveTrack' || selectedEntityId.value === 'RawDdexTrack',
);

/**
 * The generic card header's leading mark. Container entities never reach it —
 * they supply their own header — so this answers for everything else, and a
 * model with no per-record variant just gets its model mark.
 */
function titleIconForRow(row: Record<string, unknown>) {
  return modelIconForRow(selectedEntityId.value, row);
}

function coverThumbAlt(row: Record<string, unknown>): string {
  const title = (row.name as string) ?? (row.title as string) ?? `#${row.id}`;
  return `Cover art for ${title}`;
}

/**
 * Card tag chips render when the fixed card is active (tags are part of the
 * fixed card) or when the user's Column Settings include the Tags column —
 * hiding the column hides the chips. The text summary never spells tags out
 * (the chips are their representation), hence the row-level exclude below.
 */
const showCardTags = computed(
  () =>
    fixedCardComponent.value !== null ||
    cardColumns.value.some((column) => column.id === 'tags'),
);

/**
 * The rating chip (and the loud red NR) is not a tag — it must survive the
 * Tags column being toggled off. The footer renders when either should show;
 * the Tags toggle only controls the tag chips themselves.
 */
const showLiveTrackRating = computed(() => selectedEntityId.value === 'LiveTrack');

const rowExcludeFields = computed(() => [...cardExcludeFields.value, 'tags']);

/**
 * The entity's fixed card component while the Fixed Card layout is active —
 * rows render it INSTEAD of the dynamic column summary. Null in every other
 * layout (and for entities without a fixed card, where the option is not
 * offered and a persisted preference falls back to plain cards).
 */
const fixedCardComponent = computed(() =>
  tableLayout.value === 'fixed-cards'
    ? fixedCardHeaderComponentFor(selectedEntityId.value)
    : null,
);

/**
 * A bucket or a custom list gets the two-line container header instead of the
 * generic title row. It must stay in step with `cardFixedColumnIdsFor`, which
 * pins the fields that header draws: pin without drawing and the zone, the
 * location and the parent bucket leave the summary line with nothing showing
 * them and no checkbox left to bring them back.
 */
const isContainerCard = computed(() => isPlaylistContainerEntity(selectedEntityId.value));

/**
 * Master-list cards render the CARD-visible columns (opt-in, defaults-only
 * until the user turns more on — see cardVisibleColumns); the table's own
 * column visibility is untouched. Custom display columns (play,
 * currently_playing) render as rich blocks, not summary text.
 *
 * Card-fixed columns are excluded: the card header always shows them, and
 * Column Settings pins them checked-and-disabled in card layouts — a
 * previously persisted visibility entry (e.g. Account's Name before it was
 * pinned) would otherwise print the value a second time in the summary with
 * no toggle left to turn it off.
 */
const cardColumns = computed(() => {
  const fixedColumnIds = new Set(cardFixedColumnIdsFor(selectedEntityId.value));
  return cardVisibleColumns.value.filter(
    (column) => column.dataType !== 'display' && !fixedColumnIds.has(column.id),
  );
});

/** The LZP "Currently Playing" block — a card-visible custom column, on by default. */
const currentlyPlayingVisible = computed(
  () =>
    isLocationZonePlayer.value &&
    cardVisibleColumns.value.some((column) => column.id === 'currently_playing'),
);

/** The inspector always shows every field, grouped — independent of Column Settings. */
const inspectorColumns = computed(() => columns.value);

/**
 * Fields the card header already shows. For Location Zone Player the header is
 * fixed (status light, id, location, zone, time since check-in) and always
 * visible regardless of Column Settings; everything else comes and goes with
 * column visibility. Shared with every inspector surface via the registry.
 */
const cardExcludeFields = computed(() => inspectorExcludeFieldsFor(selectedEntityId.value));


/** Once true, keep `SearchMapView` mounted and toggle with `v-show` so MapKit is not torn down (blank map on return). */
const mapHostReady = ref(false);

watch(
  () => [canUseMapView.value, effectiveViewMode.value] as const,
  ([canMap, mode]) => {
    if (!canMap) {
      mapHostReady.value = false;
      return;
    }
    if (mode === 'map') {
      mapHostReady.value = true;
    }
  },
  { immediate: true, flush: 'sync' },
);

// Cursor (search_after) paging — any page (incl. Last) without tripping the 10k window (TABASCO-1HT).
const { cursor, pageNumber, cachedTotal, hasNext, hasPrev, reset: resetPager, captureMeta, navigate: navigateHandler } =
  useCursorPager(pageSize);

// Register this reset BEFORE useSearchApi so an entity/sort/filter/page-size change nulls the cursor
// before vue-query's options watcher (registered inside the composable) reacts — otherwise the first
// refetch goes out with the previous page's token under the new query (stale request + double fetch).
watch(
  () => JSON.stringify([entitySlug.value, columnSort.value, compositeFilter.value, pageSize.value]),
  () => resetPager(),
);

const {
  data,
  isLoading,
  isError: isSearchError,
} = useSearchApi({
  entitySlug,
  cursor,
  pageSize,
  sorting: columnSort,
  filter: compositeFilter
});
watch(data, () => captureMeta(data.value?.meta));

// Quick search (SAU-467): which fields each row matched, for the cells and cards to
// highlight, and the one extra line under a row whose matched field is not on screen.
const quickSearchMatches = provideQuickSearchMatches({
  entityId: () => selectedEntityId.value,
  text: () => listSearchText(tableFilter.value),
  meta: () => data.value?.meta,
});
const tableShownColumns = computed(() =>
  [...visiblePinnedColumns.value, ...visibleNonPinnedColumns.value].filter((column) => column.id !== 'play' && !column.cardOnly),
);
const tableShownFieldKeys = computed(() => new Set(tableShownColumns.value.map((column) => column.key)));
/** The extra line goes under the row's first column, so it reads as part of the row. */
const firstTableColumnId = computed(() => tableShownColumns.value[0]?.id ?? null);
function cellMatched(row: Record<string, unknown>, columnKey: string): boolean {
  return quickSearchMatches.matchesFor(row.id).some((match) => match.field === columnKey);
}
function tableHiddenMatch(row: Record<string, unknown>) {
  return hiddenMatch(quickSearchMatches.matchesFor(row.id), tableShownFieldKeys.value);
}
/** Shared by every row with no match, so the prop stays the same array between redraws. */
const NO_MATCHED_FIELDS: string[] = [];
/**
 * Each row's matched fields, built again only when the rows or the matches change. A
 * card's header and slots get the same array on every page redraw, so selecting a card
 * does not redraw them all.
 */
const cardMatchedFieldsById = computed(() => {
  const fieldsById = new Map<string, string[]>();
  for (const row of displayRows.value) {
    const matches = quickSearchMatches.matchesFor(row.id);
    if (matches.length > 0) {
      fieldsById.set(String(row.id), matches.map((match) => match.field));
    }
  }
  return fieldsById;
});
/**
 * The fields this card row matched, for its header and slots to highlight. The page
 * passes them in, and the components never read the matches by row id themselves,
 * because they are drawn elsewhere for other records too.
 */
function cardMatchedFields(row: Record<string, unknown>): string[] {
  return cardMatchedFieldsById.value.get(String(row.id)) ?? NO_MATCHED_FIELDS;
}

// Live polling: re-fetch the visible rows by id on the inspector's cadence and
// merge fresh values in place (size-gated). `displayRows` is what every view
// renders — same rows in the same order as `data`, just with live cell values.
const {
  state: livePollState,
  payloadBytes: livePollPayloadBytes,
  maxPayloadBytes: livePollMaxPayloadBytes,
  pollIntervalMs: livePollIntervalMs,
  lastUpdatedAt: livePollLastUpdatedAt,
  displayRows,
  isRowMissing,
} = useSearchResultsLivePolling({
  entitySlug,
  searchData: data,
  // Bridge open-inspector record polls into the row list (keeps the detail
  // pane and its row in lockstep — the inspector polls faster).
  recordEntityId: selectedEntityId,
});

/**
 * The group headers for the card lists. The table works out its own from the same
 * helper. The cards look the column up among all columns, because the group column
 * need not be one of the fields a card shows.
 */
const cardGroupHeaders = computed(() => groupHeadersFor(displayRows.value, groupBy.value, columns.value));

// Batched now-playing lookup for the LZP cards/cells (empty rows = no fetch).
const currentTrackRows = computed(() =>
  selectedEntityId.value === 'LocationZonePlayer' ? displayRows.value : [],
);
const { summaryForRow: currentTrackSummaryForRow } = useCurrentTrackSummaries(currentTrackRows);

/**
 * The matched fields the card's header and slots draw for this row, so the card adds
 * no extra line for them. The flags are the ones the card template below uses to decide
 * what it draws.
 */
function cardShownFieldKeys(row: Record<string, unknown>): string[] {
  if (quickSearchMatches.matchesFor(row.id).length === 0) {
    return [];
  }
  return cardShownMatchFields(selectedEntityId.value, row, {
    fixedCard: fixedCardComponent.value !== null,
    tags: showCardTags.value,
    rating: showLiveTrackRating.value,
    currentlyPlaying: currentlyPlayingVisible.value,
    currentTrackResolved: currentlyPlayingVisible.value && currentTrackSummaryForRow(row) !== null,
  });
}

// --- Cards master-detail (Apple Mail style) ---
const inspectorStore = useInspectorStore();

// Keep the side-peek inspector below the toolbar row so the list search,
// filters, and paginator stay visible and clickable while it is open.
const toolbarRef = ref<HTMLElement | null>(null);
useInspectorTopOffset(toolbarRef);
const isDesktop = useMediaQuery('(min-width: 768px)');
const isSplitViewActive = computed(() => effectiveViewMode.value === 'cards' && isDesktop.value);
/** The table shows its selected record below it: a desktop table on a list route. */
const isTablePaneActive = computed(
  () => onEntityListRoute && effectiveViewMode.value === 'list' && isDesktop.value,
);

const CUSTOM_LIST_ENTITY_ID = 'LocationZoneCustomPlaylist';

/**
 * Custom list cards keep today's clicks and ticks until their own sub-issue (spec rule 22). A plain
 * click there opens the list in the pane without touching the ticks, so the pane keeps its own id.
 */
const isCustomListCards = computed(
  () => selectedEntityId.value === CUSTOM_LIST_ENTITY_ID && effectiveViewMode.value === 'cards',
);

// --- The page's one selection, for every entity, in the table and the cards ---
// Spec: docs/specs/sau-599-select-without-checkboxes.md.

/** The ids of the rows on screen, in order. A Shift-click range and Cmd-A read them. */
const pageRowIds = computed(() =>
  (displayRows.value as Record<string, unknown>[])
    .map((row) => toBulkSelectionId(row.id))
    .filter((id): id is number => id !== null),
);

// The same inputs that reset the pager, minus the page size. A new page size shows the same
// matches cut differently, so the rows the user picked are still the rows they meant.
// Both selections share it, so a new filter, sort or entity clears the rows and the schedules.
const bulkResetKey = computed(() => JSON.stringify([entitySlug.value, columnSort.value, compositeFilter.value]));
/** Built the same way the search request builds its filter, so "all matching" names the rows on screen. */
const bulkSearchFilter = computed(() => buildSearchFilterPayload(compositeFilter.value) ?? null);
const bulkSearchInputs = () => ({ filter: bulkSearchFilter.value, sort: columnSort.value });

const bulkSelection = useBulkSelection({
  pageRowIds,
  total: cachedTotal,
  searchInputs: bulkSearchInputs,
  resetKey: bulkResetKey,
});

/**
 * The row the pane shows when the selection does not say which: on custom list cards, for a row
 * whose id is not a whole number, which cannot join the selection, and for the row of a related
 * link clicked while the bar shows, which leaves that selection alone.
 */
const paneRowIdOutsideSelection = ref<string | null>(null);

/**
 * The row the pane shows. Unless a row outside the selection holds the pane, it is read from the
 * selection: the one selected row, or none while nothing or several rows are selected (spec rule
 * 12). Writing it selects that row alone, or clears the selection, so the pane's ×, the trail, the
 * arrow keys and a related link all work through it. One exception: while the bar shows, ↑ and ↓
 * call `bulkSelection.selectOnly` themselves, so the row they move to stays selected on purpose and
 * the bar stays (spec rule 16). A change to the rules here must be made in the keydown handler's
 * `move` case too.
 */
const selectedRowId = computed<string | null>({
  get() {
    if (isCustomListCards.value || paneRowIdOutsideSelection.value !== null) return paneRowIdOutsideSelection.value;
    const soleId = bulkSelection.soleSelectedId.value;
    return soleId === null ? null : String(soleId);
  },
  set(rowId) {
    if (isCustomListCards.value) {
      paneRowIdOutsideSelection.value = rowId;
      return;
    }
    // Closing a link's row that a selection on purpose let into the pane keeps that selection.
    if (rowId === null && paneRowIdOutsideSelection.value !== null && bulkSelection.showsBar.value) {
      paneRowIdOutsideSelection.value = null;
      return;
    }
    if (rowId !== null && toBulkSelectionId(rowId) !== null) {
      paneRowIdOutsideSelection.value = null;
      bulkSelection.selectOnly(rowId);
      return;
    }
    bulkSelection.clear();
    paneRowIdOutsideSelection.value = rowId;
  },
});

/** Drops the selection and the pane's row together. */
function clearListSelection() {
  bulkSelection.clear();
  paneRowIdOutsideSelection.value = null;
}

// Any change to the selection gives the pane back to it, so a Cmd-click after a related link
// shows the selection again. Clearing the selection does too, then the setter above puts back a
// row that could not join it.
watch(
  [() => bulkSelection.mode.value, () => bulkSelection.chosenIds.value, () => bulkSelection.excludedIds.value],
  () => {
    if (!isCustomListCards.value) paneRowIdOutsideSelection.value = null;
  },
  { flush: 'sync' },
);
// Resolve the selection against displayRows so the inline detail pane opens
// with the freshest polled values (same ids/order as the raw search rows).
const selectedRow = computed(
  () =>
    displayRows.value.find(
      (row: Record<string, unknown>) => String(row.id) === selectedRowId.value,
    ) ?? null,
);

/**
 * The custom list a schedule row opens. A schedule is always read in its list, so a click on a
 * schedule opens the list's inspector on Schedules, with that schedule highlighted, rather than
 * a page of the schedule's own columns. Null for any other entity.
 */
function scheduleListIdFor(row: Record<string, unknown> | null): number | null {
  if (selectedEntityId.value !== 'LocationZoneCustomPlaylistSchedule' || row === null) return null;
  const listId = Number(row.location_zone_custom_playlist_id);
  return Number.isInteger(listId) && listId > 0 ? listId : null;
}

/** Ask the list's inspector to open on Schedules and point at this schedule. */
function requestScheduleInList(row: Record<string, unknown>, listId: number): void {
  requestPlaylistContainerSection(CUSTOM_LIST_ENTITY_ID, listId, 'schedules', row.id);
}

/** The selected schedule's list, when the split view shows a list for a schedule. */
const selectedScheduleListId = computed(() => scheduleListIdFor(selectedRow.value));

/** Point the list's inspector at this row's schedule, when the row is a schedule. */
function pointScheduleListAt(row: Record<string, unknown> | null) {
  const scheduleListId = scheduleListIdFor(row);
  if (row !== null && scheduleListId !== null) requestScheduleInList(row, scheduleListId);
}

// One selected schedule shows its list on Schedules, pointed at it, however it came to be the one
// selected: a plain click, or a Cmd-click that brought the selection down to it (spec rule 13).
watch(
  () => bulkSelection.soleSelectedId.value,
  (soleId) => {
    if (soleId === null) return;
    const row = (displayRows.value as Record<string, unknown>[]).find((candidate) => toBulkSelectionId(candidate.id) === soleId);
    pointScheduleListAt(row ?? null);
  },
  { flush: 'sync' },
);

/**
 * The pane's trail: the records a related link moved the pane to, after the
 * selected row. Selecting another row starts it fresh. `sync`, so a row
 * selected by a link in that row is cleared before the link's record is added.
 * Spec: docs/specs/record-navigation-layers.md, "The pane's trail".
 */
const paneTrail = createPaneTrail();
watch(selectedRowId, (rowId) => paneTrail.startFreshFor(rowId), { flush: 'sync' });

/** Whether the list shows its record in a pane: a desktop table, or desktop cards. */
const hasRecordPane = computed(() => isTablePaneActive.value || isSplitViewActive.value);

/**
 * A related record's link in a table cell or on a card selects its row, and
 * moves the pane on from that row to the record. A link to the row's own
 * record, such as the ID cell's peek button, only selects the row.
 * While the bar shows a selection made on purpose, the link leaves it alone and
 * the pane shows the link's row outside it (spec rule 7). While the combined
 * record shows, the pane is not the list's to move, so a link opens the side
 * peek instead.
 */
provide(LIST_PANE_HOST, {
  isActive: () => hasRecordPane.value && !showsCombinedRecord.value,
  showFromRow(rowId, entityId, recordId, options) {
    const row = (displayRows.value as Record<string, unknown>[]).find((candidate) => String(candidate.id) === rowId);
    if (!row) return;
    if (!isCustomListCards.value && bulkSelection.showsBar.value) {
      paneRowIdOutsideSelection.value = rowId;
    } else {
      selectRowForPane(row);
    }
    paneTrail.clear();
    if (entityId === selectedEntityId.value && String(recordId) === rowId) return;
    paneTrail.push({
      entityId,
      recordId,
      title: `${entityDisplayNameFor(entityId)} #${recordId}`,
      ...(options?.initialSection ? { initialSection: options.initialSection } : {}),
    });
  },
});

/** Selects a row so the pane shows it, as a plain click on the row does. */
function selectRowForPane(row: Record<string, unknown>) {
  const id = String(row.id);
  // Selecting the row already shown points its schedule list again. The watcher above only sees a change.
  if (selectedRowId.value === id) pointScheduleListAt(row);
  selectedRowId.value = id;
}

/**
 * Drop the pane's row when the visible rows change, so we never highlight a row that's no longer
 * rendered. A row chosen on purpose stays in the selection, so a bulk action still covers it.
 */
watch(
  () => data.value?.data ?? [],
  (rows: Record<string, unknown>[]) => {
    if (selectedRowId.value === null) return;
    const paneRowIsOutsideSelection = paneRowIdOutsideSelection.value !== null;
    if (!isCustomListCards.value && !paneRowIsOutsideSelection && bulkSelection.deliberate.value) return;
    if (!rows.some((row) => String(row.id) === selectedRowId.value)) {
      selectedRowId.value = null;
    }
  },
);

// --- Players: the same selection, acted on by the issue bar rather than the selection bar ---
const isLocationZonePlayer = computed(() => selectedEntityId.value === 'LocationZonePlayer');
const hasSyncPill = computed(() => showsSyncPill(selectedEntityId.value));

/**
 * The selected players among the loaded records. The issue bar can act only on these. A player that
 * live polling marked as missing is left out, so Cmd-A or a click never makes a dimmed player
 * commandable. A plain click still opens a dimmed player in the pane, as on main.
 */
const selectedPlayerRecords = computed(() =>
  (displayRows.value as Record<string, unknown>[]).filter(
    (row) => bulkSelection.isSelected(row.id) && !isRowMissing(row),
  ),
);

// Drop selected players that scrolled out of the result set. Also drop players that live polling
// flagged as missing (deleted or de-scoped) from a selection made on purpose. A player picked by a
// plain click stays while it is dimmed, so its pane stays open across polls, as on main.
// selectedPlayerRecords keeps a dimmed player out of the issue bar either way, so it is never
// commandable. Watches displayRows so a poll response re-runs the prune. A new entity clears the
// selection through its reset key.
watch(
  [displayRows, isLocationZonePlayer],
  ([rows, isLzp]) => {
    if (!isLzp) return;
    const keepsDimmedRows = !bulkSelection.deliberate.value;
    const present = new Set(
      (rows as Record<string, unknown>[])
        .filter((row) => keepsDimmedRows || !isRowMissing(row))
        .map((row) => toBulkSelectionId(row.id))
        .filter((id): id is number => id !== null),
    );
    if ([...bulkSelection.chosenIds.value].some((id) => !present.has(id))) {
      bulkSelection.retainOnly(present);
    }
  },
);

// --- Bulk actions: custom lists only, in both the cards and the table, schedules on the cards only ---
// Every entity selects rows. Only custom lists have bulk actions. Schedules are searched through
// their custom list, so the custom list page keeps a second selection for its schedules.
const BULK_ACTION_ENTITY_IDS = [CUSTOM_LIST_ENTITY_ID];
const isBulkActionEntity = computed(() => BULK_ACTION_ENTITY_IDS.includes(selectedEntityId.value));
const { can } = usePermissions();
// A user who may list custom lists but not their schedules gets the lists alone. The schedule
// count endpoint and the schedule bulk actions would refuse them.
// The table draws no schedule checkboxes, so it offers the lists alone too.
const schedulesSelectable = computed(
  () =>
    selectedEntityId.value === CUSTOM_LIST_ENTITY_ID &&
    effectiveViewMode.value === 'cards' &&
    can('view', 'location_zone_custom_playlist_schedule'),
);

/**
 * The schedules each custom list on screen draws, by row id. It is worked out once per row here.
 * The card, the table cell and the shift-click range below all read it, so a row is never worked
 * out three times. It moves on with the schedule clock, like {@link cardSchedulesFor} does.
 */
const cardSchedulesByRowId = computed<ReadonlyMap<string, ScheduleEntry[]>>(() => {
  const byRowId = new Map<string, ScheduleEntry[]>();
  if (selectedEntityId.value !== CUSTOM_LIST_ENTITY_ID) return byRowId;
  for (const row of displayRows.value as Record<string, unknown>[]) {
    byRowId.set(cardSchedulesKeyOf(row), cardSchedulesFor(row));
  }
  return byRowId;
});

/**
 * The ids of the schedules on screen, in the order the cards draw them: each row's
 * card schedules, row after row. A shift-click on a schedule ranges over this order.
 */
const pageScheduleIds = computed(() =>
  schedulesSelectable.value
    ? [...cardSchedulesByRowId.value.values()]
        .flatMap((entries) => entries.map((entry) => toBulkSelectionId(entry.schedule.id)))
        .filter((id): id is number => id !== null)
    : [],
);

/** How many schedules pass the search. Asked for only once the bulk bar's top row shows. */
const matchingScheduleCount = useMatchingScheduleCount({ resetKey: bulkResetKey, filter: bulkSearchFilter });

// A separate selection from the lists', because list 5 and schedule 5 are different rows.
// Its "select all matching" sends `list_search`: the schedules that pass this custom list search.
const scheduleSelection = useBulkSelection({
  pageRowIds: pageScheduleIds,
  total: () => matchingScheduleCount.count.value ?? 0,
  searchInputs: bulkSearchInputs,
  resetKey: bulkResetKey,
  selectAllMode: 'list_search',
});

// A switch between Table, Cards and Map clears the selection (spec rule 18): the rows, the pane's
// row and the ticked schedules. The table cannot show schedules, so a table-view action must not
// act on schedules the user can no longer see.
watch(effectiveViewMode, () => {
  clearListSelection();
  scheduleSelection.clear();
});

/** The bar's top row shows from the first ticked list or schedule. That is when the schedules are counted. */
const bulkTopRowShows = computed(
  () => schedulesSelectable.value && (bulkSelection.hasSelection.value || scheduleSelection.hasSelection.value),
);
watch(bulkTopRowShows, (topRowShows) => {
  if (topRowShows) matchingScheduleCount.request();
});

/**
 * A bulk run ended, so it may have deleted or changed schedules that pass this search. The old
 * count would show a wrong number, and select all would send it as a stale `expected_count`.
 */
function onBulkRunFinished() {
  // Read the flag before the count is dropped. With every matching schedule selected, the
  // selection's size comes from the count, so dropping the count makes the flag read false.
  const topRowShowed = bulkTopRowShows.value;
  matchingScheduleCount.invalidate();
  if (topRowShowed) matchingScheduleCount.request();
}

/**
 * The ticked schedules among those on screen, for the checkboxes. It covers "all matching" too.
 *
 * It is one Set for the life of the page, changed in place. Every card gets this same Set, so a
 * tick on schedule 9001 redraws only the line that asks about 9001. A new Set on each tick would
 * redraw every schedule line on the page.
 */
const selectedScheduleIds = reactive(new Set<number>());
const selectedScheduleIdsOnPage = computed(() => pageScheduleIds.value.filter((id) => scheduleSelection.isSelected(id)));
watch(
  selectedScheduleIdsOnPage,
  (tickedIds) => {
    const ticked = new Set(tickedIds);
    for (const id of [...toRaw(selectedScheduleIds)]) {
      if (!ticked.has(id)) selectedScheduleIds.delete(id);
    }
    for (const id of ticked) {
      if (!toRaw(selectedScheduleIds).has(id)) selectedScheduleIds.add(id);
    }
  },
  { immediate: true, flush: 'sync' },
);

function onToggleScheduleSelect(scheduleId: number, event: MouseEvent) {
  scheduleSelection.toggle(scheduleId, event);
}

// The cards, the table's Schedules cell and the inspector read each row's schedules from here.
// The table cell only shows schedules. It never ticks them, so it needs nothing else.
provideCardSchedules(cardSchedulesByRowId);

/** What the bulk bar needs of the schedule selection. Null on any entity without one. */
const scheduleBarSelection = computed<BulkBarScheduleSelection | null>(() =>
  schedulesSelectable.value
    ? {
        mode: scheduleSelection.mode.value,
        count: scheduleSelection.count.value,
        excludedCount: scheduleSelection.excludedIds.value.size,
        total: matchingScheduleCount.count.value,
        totalFailed: matchingScheduleCount.isError.value,
        canSelectAllMatching: matchingScheduleCount.count.value !== null && scheduleSelection.hasUntickedMatches.value,
      }
    : null,
);

/**
 * "Select all matching N" beside the count. On the custom list search the offer names lists and
 * schedules, and shows after a ticked schedule too, so it asks only whether more lists match.
 */
const canSelectAllMatchingLists = computed(() =>
  schedulesSelectable.value ? bulkSelection.hasUntickedMatches.value : bulkSelection.canSelectAllMatching.value,
);

/**
 * The bar's clear. With no kinds named, as from its clear button, both selections are cleared.
 * After an action starts it names the kinds the action covered. Add tag, for example, covers the
 * lists only, so the ticked schedules stay ticked.
 */
function clearBulkSelections(kinds?: MixedRunKind[]) {
  if (!kinds || kinds.includes('lists')) bulkSelection.clear();
  if (!kinds || kinds.includes('schedules')) scheduleSelection.clear();
}

/** Whether a row or card is in the selection, for its tint and `aria-selected`. */
function isRowSelected(row: Record<string, unknown>): boolean {
  return bulkSelection.isSelected(row.id);
}

/** A custom list card's checkbox. It ticks as today and never opens or closes the pane (spec rule 22). */
function onTickCustomListCard(row: Record<string, unknown>, event: MouseEvent) {
  bulkSelection.toggle(row.id, event);
}

/**
 * The selection bar's count, × and offer. It shows for two or more rows, or one picked on purpose
 * (spec rule 16). On custom list cards a ticked schedule shows it too.
 */
const bulkBarShowsSelection = computed(
  () => bulkSelection.showsBar.value || scheduleSelection.hasSelection.value,
);

/**
 * The records the person has selected, for the combined record. `count` is how many are
 * selected, and `rows` are the loaded rows among them. It reads the one selection every results
 * view shares, which a custom list card's checkbox ticks too. The ticked schedules are never a
 * source: several schedules keep the behaviour they have on main.
 *
 * `rows` holds only the loaded rows, so a record selected on another page, or after "Select all
 * matching", makes it shorter than `count`. A record marked missing empties it. Either way the
 * combined record shows the count alone, and never values worked out from part of the selection.
 * Spec: docs/specs/sau-600-combined-record.md.
 */
const selectedRecords = computed<{ rows: Record<string, unknown>[]; count: number }>(() => {
  const count = bulkSelection.count.value;
  let rows = (displayRows.value as Record<string, unknown>[]).filter((row) => bulkSelection.isSelected(row.id));
  if (rows.some((row) => isRowMissing(row))) rows = [];
  return { rows, count };
});

/** Two or more selected records show the combined record in place of the single one. */
const showsCombinedRecord = computed(() => selectedRecords.value.count >= 2);

/**
 * On custom list cards the pane's row is apart from the ticks (spec rule 22), so the single record
 * and the combined record take turns there by hand. Everywhere else the pane's row is the
 * selection's sole row, and the turns follow from that.
 *
 * Reaching two ticked lists drops the single record. Its trail and its tab go with it, so they do
 * not come back later. Dropping to one shows the remaining list on its own, as if it had been
 * clicked. Unticking that last list closes the pane. A plain click since then chose another list,
 * and that list stays. `collapsedToRowId` is cleared once nothing is ticked, so a later tick and
 * untick never closes a list the person chose with a plain click.
 */
const collapsedToRowId = ref<string | null>(null);
watch(
  () => selectedRecords.value.count,
  (count, previousCount) => {
    if (!isCustomListCards.value) return;
    if (count >= 2) {
      selectedRowId.value = null;
      return;
    }
    if (count === 1 && previousCount >= 2) {
      const remainingRow = selectedRecords.value.rows[0];
      if (!remainingRow) return;
      selectedRowId.value = String(remainingRow.id);
      collapsedToRowId.value = selectedRowId.value;
      return;
    }
    if (count === 0) {
      if (selectedRowId.value !== null && selectedRowId.value === collapsedToRowId.value) {
        selectedRowId.value = null;
      }
      collapsedToRowId.value = null;
    }
  },
);

/**
 * The combined record's close button. It clears the selection that opened it. The ticked
 * schedules stay ticked.
 */
function closeCombinedRecord() {
  clearListSelection();
}

/**
 * Split view renders its detail inline, so close any stale overlay inspector
 * when it becomes active (e.g. one carried over from another page); leaving
 * split view just drops the inline selection.
 */
watch(
  isSplitViewActive,
  (active) => {
    if (active) {
      if (inspectorStore.isOpen) {
        inspectorStore.close();
      }
    } else {
      selectedRowId.value = null;
    }
  },
  { immediate: true },
);

// Leaving the table's record pane, by switching to cards or map, or by narrowing
// to a phone, drops its selection.
watch(isTablePaneActive, (active) => {
  if (!active && !isSplitViewActive.value) selectedRowId.value = null;
});

// Resizing across 768 pixels clears the selection, so a row picked with a plain click on a wide
// screen does not turn the next narrow-screen tap into a toggle (spec rule 8). Custom list cards
// keep their ticks, as today.
watch(isDesktop, () => {
  if (isCustomListCards.value) return;
  clearListSelection();
});

/** The table's selected row id, for its highlight. Only while the record shows below it. */
const tableSelectedRowId = computed(() => (isTablePaneActive.value ? selectedRowId.value : null));

// Esc steps back along the trail only while the pane shows a row, since the trail hangs off that row.
const keysHaveTrail = computed(() => hasRecordPane.value && selectedRowId.value !== null && paneTrail.steps.value.length > 0);
// Esc has something to clear: selected rows, or a pane row outside the selection, such as one whose
// id is not a whole number. Custom list cards keep today's keys, so Esc never clears their ticks.
const keysHaveSelection = computed(
  () => !isCustomListCards.value && (bulkSelection.hasSelection.value || selectedRowId.value !== null),
);
// The row ↓ and ↑ move from. None on custom list cards. While rows are selected on purpose, it is the
// one selected row, or none while several are (rule 6): a related link may have put the pane on a
// row outside that selection, and moving from it would replace the selection. Otherwise it is the
// pane's row, which may sit outside the selection when its id is not a whole number.
const keysSoleSelectedId = computed(() => {
  if (isCustomListCards.value) return null;
  if (bulkSelection.showsBar.value) {
    const soleId = bulkSelection.soleSelectedId.value;
    return soleId === null ? null : String(soleId);
  }
  return selectedRowId.value;
});
// Cmd-A selects every loaded row only on a wide screen, and never on custom list cards or the map,
// which keep today's keys.
const keysAllowSelectAll = computed(
  () => isDesktop.value && !isCustomListCards.value && effectiveViewMode.value !== 'map',
);

/**
 * The list's keys, in the table and in the cards (spec rules 4 to 6). Cmd-A selects every loaded
 * row. Esc steps back along the pane's trail, and otherwise clears the selection. ↓ and ↑ move the
 * table's one selected row, and the record below follows. See tableSelectionKeys for when the
 * list leaves a key alone. On custom list cards only the trail's Esc works, as today.
 */
useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  const action = listSelectionKeyAction(event, {
    hasTrail: keysHaveTrail.value,
    hasSelection: keysHaveSelection.value,
    soleSelectedId: keysSoleSelectedId.value,
    rowIds: pageRowIds.value.map(String),
    allowArrows: effectiveViewMode.value === 'list' && isDesktop.value,
    allowSelectAll: keysAllowSelectAll.value,
    inspectorIsOpen: inspectorStore.isOpen,
  });
  if (action === null) return;
  event.preventDefault();
  switch (action.kind) {
    case 'stop':
      // An arrow at the first or last row does nothing, and it does not scroll the table either.
      return;
    case 'trailBack':
      paneTrail.back();
      return;
    case 'clear':
      clearListSelection();
      return;
    case 'selectAll':
      bulkSelection.selectAllLoaded();
      return;
    case 'move':
      if (bulkSelection.showsBar.value) {
        // A row selected on purpose stays on purpose when it moves, so the bar stays (rule 16).
        paneRowIdOutsideSelection.value = null;
        bulkSelection.selectOnly(action.rowId, { onPurpose: true });
      } else {
        selectedRowId.value = action.rowId;
      }
      void nextTick(() => {
        document
          .querySelector('[data-testid="search-table"] [data-active-row="true"]')
          ?.scrollIntoView({ block: 'nearest' });
      });
      return;
  }
});

/**
 * Opens a row on its own, as a tap does on a narrow screen. A schedule has no page of its own: it
 * is read in its list, so its list opens in the peek, on Schedules, with the schedule highlighted.
 * Off a list route, as in a bare test mount, a row peeks.
 */
function openRowOnItsOwn(row: Record<string, unknown>) {
  const scheduleListId = scheduleListIdFor(row);
  if (scheduleListId !== null) {
    openRecord(CUSTOM_LIST_ENTITY_ID, scheduleListId, { initialSection: 'schedules' });
    return;
  }
  if (listRouter) {
    openRecordPage(row);
  } else {
    openRecord(selectedEntityId.value, row.id as number | string);
  }
}

/**
 * A click on a table row or a card, for every view except custom list cards.
 * On a wide screen a plain click selects the row alone and the pane shows it, Cmd-click adds or
 * removes it, and Shift-click extends to it. On a narrow screen a tap opens the row while nothing
 * is selected, and adds or removes it once a long press has started a selection.
 */
function applyRowGesture(row: Record<string, unknown>, gesture: RowGesture) {
  const bulkId = toBulkSelectionId(row.id);
  // A row whose id is not a whole number cannot join the selection. It shows in the pane or opens, as today.
  if (bulkId === null) {
    if (!hasRecordPane.value) {
      openRowOnItsOwn(row);
      return;
    }
    selectedRowId.value = String(row.id);
    paneTrail.clear();
    return;
  }

  const resolved = resolveRowGesture(gesture, { narrow: !isDesktop.value, hasSelection: bulkSelection.hasSelection.value });
  switch (resolved) {
    case 'open':
      openRowOnItsOwn(row);
      return;
    case 'replace':
      selectRowForPane(row);
      // Clicking the row already selected also starts the pane fresh on it.
      paneTrail.clear();
      return;
    case 'toggle':
      // A count in a card asks for a section before its click reaches the card. This click only selects.
      dropPlaylistContainerRequest();
      bulkSelection.toggleOne(bulkId);
      return;
    case 'extend':
      dropPlaylistContainerRequest();
      bulkSelection.extendTo(bulkId);
      return;
  }
}

/**
 * A plain click on a custom list card opens the list in the pane, as today, without changing
 * the ticks (spec rule 22). On a narrow screen it opens the list's page.
 */
function onCustomListCardSelect(row: Record<string, unknown>) {
  if (isSplitViewActive.value) {
    // The combined record keeps the pane while two or more lists are ticked.
    if (showsCombinedRecord.value) return;
    selectedRowId.value = String(row.id);
    paneTrail.clear();
    return;
  }
  openRowOnItsOwn(row);
}

function onCardSelect(row: Record<string, unknown>, _event: MouseEvent | undefined, gesture: RowGesture) {
  if (isCustomListCards.value) {
    onCustomListCardSelect(row);
    return;
  }
  applyRowGesture(row, gesture);
}

// A long press with a finger adds a row to the selection on a narrow screen. A long press on a row
// already selected keeps it selected (spec rule 9). One listener on the card list and one on the
// table cover every row in them. Custom list cards keep today's taps.
const phoneCardListRef = ref<HTMLElement | null>(null);
const tableViewRef = ref<HTMLElement | null>(null);
function isLoadedRowId(rowId: string): boolean {
  const bulkId = toBulkSelectionId(rowId);
  return bulkId !== null && pageRowIds.value.includes(bulkId);
}
for (const container of [phoneCardListRef, tableViewRef]) {
  useLongPressDelegate({
    container,
    enabled: () => !isDesktop.value && !isCustomListCards.value,
    isRowLoaded: isLoadedRowId,
    onLongPress: (rowId) => bulkSelection.selectOne(rowId),
  });
}

const columnsResizeHandler = (columnId: string, width: number) => {
  setColumnSize(columnId, width);
};

const columnsSortHandler = (columnField: string) => {
  toggleSort(columnField);
};

const columnsReorderHandler = (columns: TableColumn[]) => {
  updateColumnState(columns)
};
</script>

<template>
  <!-- Zero permitted models (SAU-287): no picker, no dead table — say it plainly. -->
  <div
    v-if="hasNoPermittedEntities"
    class="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-1 p-8 text-center"
    data-testid="search-no-entities"
  >
    <p class="text-sm font-medium">No models available</p>
    <p class="text-muted-foreground text-sm">
      Your account doesn't have view access to any searchable models yet. Ask an administrator to grant access.
    </p>
  </div>
  <!--
    The page is one flat ground, as in Macro. The toolbar, the table and (on
    mobile) the card list all sit on it with no box of their own, so nothing
    reads as a separate sheet.
  -->
  <div v-else class="flex min-h-0 min-w-0 flex-1 flex-col bg-background">
    <!-- Toolbar (entity, filters, paginator, settings): stays visible above the
         scroll area. No background of its own — the ground shows through. -->
    <div ref="toolbarRef" class="z-30 shrink-0">
      <EditableSearchTableHeader
        :totalRecords="cachedTotal"
        :current-page="pageNumber"
        :has-next="hasNext"
        :has-prev="hasPrev"
        :loading="isLoading"
        :has-sync-pill="hasSyncPill"
        @navigate="navigateHandler"
      >
        <!-- The Sync pill rides with the live-polling indicator, on the lists
             showsSyncPill names. The header places the
             cluster (see searchToolbarStatusLayout). The indicator comes after the
             pill, so it sits between the pill and the pager. -->
        <template #status="{ form }">
          <div class="flex items-center gap-2">
            <SyncStatusButton v-if="hasSyncPill" :form="form" />
            <SearchLivePollingIndicator
              :state="livePollState"
              :payload-bytes="livePollPayloadBytes"
              :max-payload-bytes="livePollMaxPayloadBytes"
              :poll-interval-ms="livePollIntervalMs"
              :last-updated-at="livePollLastUpdatedAt ?? 0"
            />
          </div>
        </template>
        <!-- Export the result set on screen to a spreadsheet, whichever entity
             is being searched. Export opens the export column list, which
             starts from the columns on screen. The control renders nothing
             without the export permission. -->
        <template v-if="searchSource" #actions="{ fit }">
          <TrackListExportControls
            :source="searchSource"
            :label="searchLabel"
            :columns="exportColumnIds"
            :column-options="exportColumnOptions"
            :fit="fit"
            button-variant="toolbar"
          />
        </template>
      </EditableSearchTableHeader>
    </div>
    <!-- Explicit error variant: a failed search (e.g. a 403 on a model this
         user lost access to) must never masquerade as an empty result. It sits
         above the view branches so the table and card views say it too — an
         empty table reads as "nothing matched", which is the silent failure
         this exists to prevent. -->
    <p
      v-if="isSearchError && !isLoading"
      class="text-destructive px-4 pt-6 text-center text-sm"
      data-testid="search-error-state"
    >
      Couldn't load results. You may not have access to this model — pick another, or retry.
    </p>
    <!--
      The selection bar: the count, × and "Select all N matching", for every entity except players.
      On custom lists it also carries the bulk actions for the selected lists and schedules, and it
      follows a running bulk action and shows its report. It sits above the view branches, so the
      cards and the table both show it.
    -->
    <BulkActionBar
      v-if="!isLocationZonePlayer && entitySlug"
      class="shrink-0"
      :entity-slug="entitySlug"
      :with-actions="isBulkActionEntity"
      :show-selection="bulkBarShowsSelection"
      :mode="bulkSelection.mode.value"
      :count="bulkSelection.count.value"
      :excluded-count="bulkSelection.excludedIds.value.size"
      :total="cachedTotal"
      :can-select-all-matching="canSelectAllMatchingLists"
      :loading="isLoading"
      :build-selection="bulkSelection.buildSelectionPayload"
      :schedules="scheduleBarSelection"
      :build-schedule-selection="schedulesSelectable ? scheduleSelection.buildSelectionPayload : null"
      @clear="clearBulkSelections"
      @select-all-matching="bulkSelection.selectAllMatching()"
      @select-all-matching-schedules="scheduleSelection.selectAllMatching()"
      @run-finished="onBulkRunFinished"
    />
    <!-- Players act on their selection through the issue bar instead, in the cards and the table. -->
    <LzpBulkIssueBar
      v-if="isLocationZonePlayer && selectedPlayerRecords.length > 0"
      :players="selectedPlayerRecords"
      class="mx-3 mt-3 shrink-0"
      @clear="clearListSelection"
    />
    <!--
      Results surface: a white sheet on the ground with an even 12px gap on all
      four sides, square-cornered and hairline-bordered — the rounded corners in
      this view belong to the row cards inside it. It is also the scroll
      container, which is what `table-in-parent-scroll` + `sticky-header` need
      for a thead that sticks to the top of the results rather than the page.
    -->
    <div v-if="effectiveViewMode === 'list'" ref="tableViewRef" class="min-h-0 w-full flex-1 p-3">
      <TableRecordSplitView :has-selection="isTablePaneActive && (showsCombinedRecord || selectedRow !== null)">
        <template #table>
        <!-- The table's schedule cells order each row's lines by the sort the rows came back
             with, not the live sort. The record pane sits outside, so it keeps the card order. -->
        <TableScheduleSortScope :sort="data?.sentSort">
        <DataTable
          :key="entitySlug"
          data-testid="search-table"
          table-in-parent-scroll
          :data="displayRows"
          :group-by-field="groupBy"
          :is-row-missing="isRowMissing"
          :totalRecords="cachedTotal"
          :columns="unpinnedColumns.filter((column) => !column.cardOnly)"
          :pinnedColumns="pinnedColumns.filter((column) => !column.cardOnly)"
          :isLoading="isLoading"
          :pageIndex="pageIndex"
          :pageSize="pageSize"
          sticky-header
          multi-select
          :is-row-selected="isRowSelected"
          @select-row="applyRowGesture"
          row-id-key="id"
          :selected-row-id="tableSelectedRowId"
          @columnsSorted="columnsSortHandler"
          @columnsResized="columnsResizeHandler"
          @columnsReordered="columnsReorderHandler"
        >
          <template #cell="{ column, row }">
            <div v-if="column.id === 'play'" class="px-2 py-1">
              <PlayTrackButton v-bind="playButtonPropsForRow(row)" />
            </div>
            <ListRowPaneScope v-else :row-id="String(row.id)">
              <SearchHighlight
                :name="LIST_HIGHLIGHT"
                :terms="quickSearchMatches.terms.value"
                :active="cellMatched(row, column.key)"
                :whole="quickSearchMatches.isNumberField(column.key)"
              >
                <ColumnMapping
                  :field="column.key"
                  :dataType="column.dataType"
                  :data="row"
                />
              </SearchHighlight>
              <p
                v-if="column.id === firstTableColumnId && tableHiddenMatch(row)"
                class="truncate px-2 pb-1 text-xs text-muted-foreground"
                data-testid="list-search-hidden-match"
              >
                <SearchHighlight
                  :name="LIST_HIGHLIGHT"
                  :terms="quickSearchMatches.terms.value"
                  :whole="quickSearchMatches.isNumberField(tableHiddenMatch(row)!.field)"
                >{{ tableHiddenMatch(row)!.value }}</SearchHighlight>
              </p>
            </ListRowPaneScope>
          </template>
        </DataTable>
        </TableScheduleSortScope>
        </template>
        <template #record>
          <CombinedRecordPane
            v-if="showsCombinedRecord"
            :entity-id="selectedEntityId"
            :rows="selectedRecords.rows"
            :count="selectedRecords.count"
            @close="closeCombinedRecord"
          />
          <RecordPane
            v-else-if="selectedRow"
            :entity-id="selectedEntityId"
            :row="selectedRow"
            :schedule-list-id="selectedScheduleListId"
            :use-layout="onEntityListRoute"
            :inspector-columns="inspectorColumns"
            :trail="paneTrail"
            closable
            escape-closes
            @open="openRecordPage(selectedRow)"
            @close="selectedRowId = null"
          />
        </template>
      </TableRecordSplitView>
    </div>

    <!-- `relative`: on mobile this is the card list's scroll container, and a scroll
         container that is not positioned does not clip absolutely positioned
         descendants (an `sr-only` label is one). They belong to the layout's content
         region instead, and the ones in cards scrolled out of view sat below the
         viewport and made the whole page scroll. Desktop is covered by the split
         view's own panes, which are positioned for the same reason. -->
    <div
      v-else-if="effectiveViewMode === 'cards'"
      class="relative flex min-h-0 w-full flex-1 flex-col p-3 max-md:pb-mobile-area-bar-clearance"
      :class="isSplitViewActive ? 'overflow-hidden' : 'overflow-auto'"
    >
      <!--
        Desktop card mode: Apple Mail-style master-detail. Compact rows in the
        left pane, the entity's inspector rendered inline on the right.
      -->
      <SearchCardsSplitView
        v-if="isSplitViewActive"
        class="bg-background border-border/70 min-h-0 flex-1 overflow-hidden border"
        :rows="displayRows"
        :group-headers="cardGroupHeaders"
        :has-selection="showsCombinedRecord || selectedRow !== null"
        :is-loading="isLoading"
      >
        <template #row="{ row }">
          <ListRowPaneScope :row-id="String(row.id)">
          <SearchResultListRow
            :row="row"
            :title-icon="isContainerCard ? null : titleIconForRow(row)"
            :visible-columns="fixedCardComponent ? [] : cardColumns"
            :exclude-fields="rowExcludeFields"
            :active="String(row.id) === selectedRowId"
            :selected="isRowSelected(row)"
            :shows-checkbox="isCustomListCards"
            :long-press-selects="!isCustomListCards"
            :missing="isRowMissing(row)"
            :shown-field-keys="cardShownFieldKeys(row)"
            @select="onCardSelect"
            @toggle-select="onTickCustomListCard"
            @dblclick="openRecordPage(row)"
          >
            <template v-if="!fixedCardComponent && showCoverThumb" #leading>
              <div class="float-left mr-2.5 mb-1">
                <CoverArtTile
                  :src="(row.cover_art_signed_url as string | null) ?? null"
                  :alt="coverThumbAlt(row)"
                  :size="44"
                  :play="playButtonPropsForRow(row)"
                />
              </div>
            </template>
            <template v-if="fixedCardComponent" #header>
              <component :is="fixedCardComponent" :row="row" :matched-fields="cardMatchedFields(row)" />
            </template>
            <template v-else-if="selectedEntityId === 'LocationZonePlayer'" #header>
              <LocationZonePlayerCardHeader :row="row" :matched-fields="cardMatchedFields(row)" />
            </template>
            <template v-else-if="isContainerCard" #header>
              <PlaylistContainerCardHeader
                :row="row"
                :entity-id="selectedEntityId"
                :schedules-selectable="schedulesSelectable"
                :selected-schedule-ids="selectedScheduleIds"
                :matched-fields="cardMatchedFields(row)"
                @toggle-schedule="onToggleScheduleSelect"
              />
            </template>
            <template v-if="currentlyPlayingVisible" #subheader>
              <LzpCurrentlyPlayingCard
                :row="row"
                :track="currentTrackSummaryForRow(row)"
                :matched-fields="cardMatchedFields(row)"
              />
            </template>
            <template v-if="showCardTags || showLiveTrackRating" #footer>
              <CardTags
                :tags="showCardTags ? row.tags : undefined"
                :show-rating="showLiveTrackRating"
                :rating="row.rating"
                :matched-fields="cardMatchedFields(row)"
              />
            </template>
          </SearchResultListRow>
          </ListRowPaneScope>
        </template>
        <template #detail>
          <CombinedRecordPane
            v-if="showsCombinedRecord"
            :entity-id="selectedEntityId"
            :rows="selectedRecords.rows"
            :count="selectedRecords.count"
            @close="closeCombinedRecord"
          />
          <RecordPane
            v-else-if="selectedRow"
            :entity-id="selectedEntityId"
            :row="selectedRow"
            :schedule-list-id="selectedScheduleListId"
            :use-layout="onEntityListRoute"
            :inspector-columns="inspectorColumns"
            :trail="paneTrail"
            closable
            @open="openRecordPage(selectedRow)"
            @close="selectedRowId = null"
          />
        </template>
      </SearchCardsSplitView>
      <!-- Phone and tablet card mode: list only; tapping a row opens its full page. -->
      <div v-else ref="phoneCardListRef" data-testid="search-card-list" class="flex w-full max-w-3xl flex-col gap-2 pt-2">
        <template v-for="(row, index) in displayRows" :key="String(row.id)">
        <ListGroupHeader v-if="cardGroupHeaders.has(index)" :label="cardGroupHeaders.get(index)!" />
        <SearchResultListRow
          :row="row"
          :title-icon="isContainerCard ? null : titleIconForRow(row)"
          :visible-columns="fixedCardComponent ? [] : cardColumns"
          :exclude-fields="rowExcludeFields"
          :selected="isRowSelected(row)"
          :shows-checkbox="isCustomListCards"
          :long-press-selects="!isCustomListCards"
          :missing="isRowMissing(row)"
          :shown-field-keys="cardShownFieldKeys(row)"
          @select="onCardSelect"
          @toggle-select="onTickCustomListCard"
        >
          <template v-if="!fixedCardComponent && showCoverThumb" #leading>
            <div class="float-left mr-2.5 mb-1">
              <CoverArtTile
                :src="(row.cover_art_signed_url as string | null) ?? null"
                :alt="coverThumbAlt(row)"
                :size="44"
                :play="playButtonPropsForRow(row)"
              />
            </div>
          </template>
          <template v-if="fixedCardComponent" #header>
            <component :is="fixedCardComponent" :row="row" :matched-fields="cardMatchedFields(row)" />
          </template>
          <template v-else-if="selectedEntityId === 'LocationZonePlayer'" #header>
            <LocationZonePlayerCardHeader :row="row" :matched-fields="cardMatchedFields(row)" />
          </template>
          <template v-else-if="isContainerCard" #header>
            <PlaylistContainerCardHeader
              :row="row"
              :entity-id="selectedEntityId"
              :schedules-selectable="schedulesSelectable"
              :selected-schedule-ids="selectedScheduleIds"
              :matched-fields="cardMatchedFields(row)"
              @toggle-schedule="onToggleScheduleSelect"
            />
          </template>
          <template v-if="currentlyPlayingVisible" #subheader>
            <LzpCurrentlyPlayingCard
              :row="row"
              :track="currentTrackSummaryForRow(row)"
              :matched-fields="cardMatchedFields(row)"
            />
          </template>
          <template v-if="showCardTags || showLiveTrackRating" #footer>
            <CardTags
              :tags="showCardTags ? row.tags : undefined"
              :show-rating="showLiveTrackRating"
              :rating="row.rating"
              :matched-fields="cardMatchedFields(row)"
            />
          </template>
        </SearchResultListRow>
        </template>
        <!-- Suppressed while the shared error banner above is showing, so a
             failed search never also reads as "No results." -->
        <p
          v-if="!isSearchError && (data?.data?.length ?? 0) === 0 && !isLoading"
          class="text-muted-foreground pt-6 text-center text-sm"
        >
          No results.
        </p>
      </div>
    </div>

    <div
      v-if="canUseMapView && mapHostReady"
      v-show="effectiveViewMode === 'map'"
      class="flex min-h-0 min-w-0 flex-1 flex-col p-3"
    >
      <!-- Same sheet as the list and cards views: the map is a results surface
           too, so it gets the even gutter and the hairline edge. -->
      <div class="bg-background border-border/70 flex min-h-0 flex-1 flex-col overflow-hidden border">
        <SearchMapView :rows="displayRows" :is-loading="isLoading" />
      </div>
    </div>
  </div>
</template>
